import React, { FC, useEffect } from 'react';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Button, Divider, Stack, Typography, Box, Link } from '@mui/material';
import { HashLink } from 'react-router-hash-link';

import { LoadingIndicator } from '@pv/common/components';

import { FilterBar, FilterBarProvider } from '../../components/FilterBar';

import { fetchVenue } from '../../helpers/api/graphql';
import { ProfileHeader } from './ProfileHeader';
import { buildAvailableSpacesRequest } from '../../helpers/api/spaces';
import { buildMappableAddressCoord } from '../../helpers/api/addresses';
import { fetchAvailableSpaces } from '../../helpers/api/graphql';
import { SpacesShelf } from '../../components/SpacesShelf';
import { ProfileGoogleMap } from '../../components/ProfileGoogleMap';
import { ProfileFooter } from '../../components/ProfileFooter';
import { VenueSeoMarkup } from './VenueSeoMarkup';
import { ContactUsButton } from '../../components/ContactUsButton';
import { useAvailability } from '../../providers/AvailabilityProvider';
import {
  MarketplaceVenueQuery,
  MarketplaceVenueQueryVariables,
} from '@pv/common/graphql/operations';
import { useVenueProfile, VenueProfileContext } from './useVenueProfile';
import { VenueMenuItems } from '../../components/VenueMenuItems';

const mapStyle = {
  width: '100%',
  height: '320px',
  borderRadius: '12px',
};

const AboutVenue: FC = () => {
  const venue = useVenueProfile();

  return (
    <Stack
      direction={{
        sm: 'column-reverse',
        md: 'row',
      }}
      gap="48px"
      justifyContent="space-between"
    >
      <Stack direction="column" gap="24px" width={{ sm: '100%', md: '710px' }}>
        <Typography variant="h2" sx={{ fontWeight: '700', fontSize: '24px' }}>
          About the venue
        </Typography>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
          {venue.description ?? 'Tell us about this venue...'}
        </Typography>
      </Stack>
      <Stack
        direction="column"
        gap="16px"
        width={{ sm: '100%', md: '300px', lg: '300px' }}
      >
        <HashLink smooth to="#FindYourEventSpace">
          <Button
            variant="contained"
            color="secondary"
            sx={{ height: '50px', width: '100%', fontSize: '16px' }}
          >
            Check Availability
          </Button>
        </HashLink>
        <ContactUsButton leadFormUrl={venue.helloLeadFormUrl} />
      </Stack>
    </Stack>
  );
};

const VenueLocation: FC = () => {
  const venue = useVenueProfile();

  return (
    <Stack direction="column" gap="24px">
      <Typography
        variant="h2"
        sx={{
          fontWeight: 700,
          fontSize: '24px',
          lineHeight: '123.5%',
        }}
      >
        Location
      </Typography>
      {venue.address && (
        <ProfileGoogleMap
          mapStyle={mapStyle}
          center={buildMappableAddressCoord(venue.address)}
          mappableItems={[
            {
              key: venue.slug,
              name: venue.name,
              position: buildMappableAddressCoord(venue.address),
            },
          ]}
        />
      )}
    </Stack>
  );
};

export const VenueSpacesPage: FC = () => {
  const { venueSlug } = useParams();
  const {
    dateTimeFilter,
    setDateTimeFilter,
    capacityFilterValues,
    setCapacityFilterValues,
    startDate,
    startOffset,
    endOffset,
  } = useAvailability();

  const {
    data: venueData,
    loading: venueLoading,
    error,
  } = useQuery<MarketplaceVenueQuery, MarketplaceVenueQueryVariables>(
    fetchVenue,
    {
      variables: {
        slug: venueSlug ?? '',
      },
      skip: !venueSlug,
    },
  );

  const {
    data: availableSpacesData,
    refetch,
    loading: spacesLoading,
  } = useQuery(fetchAvailableSpaces, {
    variables: {
      venueSlug,
    },
    skip: !venueSlug,
  });

  useEffect(() => {
    if (!venueSlug) {
      return;
    }

    refetch(
      buildAvailableSpacesRequest({
        venueSlug,
        startDate,
        startOffset,
        endOffset,
        capacityFilterValues,
      }),
    );
  }, [
    refetch,
    venueSlug,
    startDate,
    startOffset,
    endOffset,
    capacityFilterValues,
  ]);

  if (venueLoading) {
    return <LoadingIndicator />;
  }

  if (error && error.graphQLErrors[0]?.message === 'No venue found') {
    return <Navigate to="/not-found" replace={false} />;
  }

  const venue = venueData?.marketplaceVenue;

  // Filter bar state for provider
  const filterBarState = {
    mode: 'profile' as const,
    dateTimeFilter,
    setDateTimeFilter,
    capacityFilterValues,
    setCapacityFilterValues,
    expressBookSettings: venue?.expressBookSettings,
    hoursOfOperation: venue?.hoursOfOperation,
  };

  if (!venue) {
    return <Navigate to="/not-found" replace={false} />;
  }

  const renderFindEventSpace = () => (
    <Stack direction="column" gap={'24px'}>
      <Typography
        id="FindYourEventSpace"
        variant="h2"
        sx={{ fontWeight: '700', fontSize: '24px' }}
      >
        Find your event space
      </Typography>

      <Box sx={{ flexGrow: 1 }}>
        <FilterBarProvider state={filterBarState}>
          <FilterBar />
        </FilterBarProvider>
      </Box>

      {spacesLoading ? (
        <LoadingIndicator />
      ) : (
        <SpacesShelf
          spaces={availableSpacesData.availableSpaces}
          gridTemplateColumns={{
            xs: '1fr',
            sm: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(2, 1fr)',
          }}
        />
      )}
    </Stack>
  );

  return (
    <VenueProfileContext.Provider
      value={{
        ...venue,
        menu: {
          previewMenuItems:
            venue.menu?.previewMenuItems.map((item) => ({
              id: item.id,
              name: item.name,
              image: item.image || undefined,
            })) ?? [],
          menuItems: venue.menu?.menuItems ?? [],
        },
        profileBanner: venue.profileBanner ?? null,
        logo: venue.logo ?? null,
        type: venue.type ?? null,
        address: venue.address
          ? {
              streetAddress1: venue.address.streetAddress1 ?? null,
              city: venue.address.city ?? null,
              state: venue.address.state ?? null,
              latitude: venue.address.latitude ?? null,
              longitude: venue.address.longitude ?? null,
            }
          : null,
        description: venue.description ?? null,
      }}
    >
      <VenueSeoMarkup venueSlug={venue.slug} />
      <header>
        <ProfileHeader />
      </header>
      <main>
        <Stack
          sx={{
            padding: { md: '24px', sm: '24px', xs: '24px', lg: '32px' },
            margin: '0px auto',
            width: {
              md: '94%',
              lg: '1200px',
            },
            gap: '32px',
          }}
        >
          <AboutVenue />
          <Divider />
          {renderFindEventSpace()}
          {venue.menu.menuItems?.length > 0 && (
            <>
              <Divider />
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                sx={{ marginBottom: '24px' }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: '700',
                    fontSize: '24px',
                    lineHeight: '133.4%',
                    marginBottom: '0',
                  }}
                >
                  {venue.name}'s Event Menu
                </Typography>
                <Link
                  component={RouterLink}
                  to={`/venues/${venue.slug}/menu`}
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 500,
                    fontSize: '16px',
                    lineHeight: '150%',
                    color: 'text.primary',
                  }}
                >
                  View Event Menu &gt;
                </Link>
              </Stack>
              <VenueMenuItems
                menuItems={venue.menu.previewMenuItems.filter(
                  (item): item is { id: string; name: string; image: string } =>
                    !!item.image,
                )}
              />
            </>
          )}

          <Divider />
          <VenueLocation />
          <Divider />
        </Stack>
      </main>
      <footer>
        <Box
          sx={{
            padding: '32px',
            margin: '0px auto',
            width: {
              md: '94%',
              lg: '1200px',
            },
            gap: '32px',
          }}
        >
          <ProfileFooter />
        </Box>
      </footer>
    </VenueProfileContext.Provider>
  );
};
